import { BusinessObjectBase } from './base';
import type { ProcessDesignActivityCount } from './process-design-activity-count';
import type { ProcessDesignHistory } from './process-design-history';
import type { ProcessInstance } from './process-instance';

export interface ProcessDesignData {
  readonly id: string;
  readonly designUuid: string;
  readonly name?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly version: number;
  readonly suspended?: boolean;
  readonly sha1Sum?: string;
  readonly designJson?: any;
  readonly analysis?: any;
  readonly processInstances?: ProcessInstance[];
  readonly processDesignHistories?: ProcessDesignHistory[];
  readonly processDesignActivityCounts?: ProcessDesignActivityCount[];
}

export interface ProcessDesign extends Readonly<ProcessDesignData> {}
export class ProcessDesign extends BusinessObjectBase {
  static readonly entityName = 'ProcessDesign' as const;
  static readonly plural = 'process-designs';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessDesign[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessDesign(data));
  }

  static async findOne(filter?: any): Promise<ProcessDesign | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessDesign(data) : null;
  }

  static async findById(id: string, filter?: any): Promise<ProcessDesign> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessDesign(data);
  }

  static async create(
    data: Omit<ProcessDesignData, 'id'>,
  ): Promise<ProcessDesign> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessDesign(result);
  }

  static async updateById(
    id: string,
    data: Partial<ProcessDesignData>,
  ): Promise<ProcessDesign> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new ProcessDesign(result);
  }

  static async deleteById(id: string): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async validateProcessDesign(design: any): Promise<Promise<void>> {
    const url = `${this.baseUrl}/${this.plural}/validate-process-design`;
    return this.post<Promise<void>>(url, design);
  }
  static async upload(options: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/upload`;
    return this.post<any>(url, options);
  }
  async suspend(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessDesign.plural}/${(this as any).id}/suspend`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async reanalyze(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessDesign.plural}/${(this as any).id}/reanalyze`;
    return BusinessObjectBase.post<any>(url, {});
  }
  static async reanalyzeOutdated(): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/reanalyze-outdated`;
    return this.post<any>(url, {});
  }
  static async reanalyzeAll(): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/reanalyze-all`;
    return this.post<any>(url, {});
  }
  static async preprocessJson(value: any): Promise<Promise<any>> {
    const url = `${this.baseUrl}/${this.plural}/preprocess-json`;
    return this.post<Promise<any>>(url, value);
  }
  static async loadFromFiles(): Promise<
    Promise<Array<{ name: string; version: number; status: string }>>
  > {
    const url = `${this.baseUrl}/${this.plural}/load-from-files`;
    return this.post<
      Promise<Array<{ name: string; version: number; status: string }>>
    >(url, {});
  }
  static async generateAnalysis(process: any): Promise<Promise<any>> {
    const url = `${this.baseUrl}/${this.plural}/generate-analysis`;
    return this.post<Promise<any>>(url, process);
  }
  static async computeSha1(data: unknown): Promise<Promise<string>> {
    const url = `${this.baseUrl}/${this.plural}/compute-sha1`;
    return this.post<Promise<string>>(url, { data });
  }
  async activate(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessDesign.plural}/${(this as any).id}/activate`;
    return BusinessObjectBase.post<any>(url, {});
  }
  static async restoreVersion(options: {
    designUuid: string;
    version: number;
  }): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/restore-version`;
    return this.post<any>(url, options);
  }
}
